export type CognitoConfig = {
  USER_POOL_ID: string;
  APP_CLIENT_ID: string;
  IDENTITY_POOL_ID: string;
  DOMAIN: string;
};

export type InjectedCognitoConfig = Partial<CognitoConfig>;

const fallbackCognitoConfig = {
  prod: {
    USER_POOL_ID: "us-east-2_MqZrrppS1",
    APP_CLIENT_ID: "7phcejhgai3vf6rj7mr526akc",
    IDENTITY_POOL_ID: "us-east-2:9ea0609d-9c44-4356-ae9e-6d38aa1898a1",
    DOMAIN: "auth-idp.assetwatch.com",
  },
  qa: {
    USER_POOL_ID: "us-east-2_tGB8JmQO3",
    APP_CLIENT_ID: "30ajgh20k1njb80coevmf9v1kr",
    IDENTITY_POOL_ID: "us-east-2:e6134d6f-3cbb-4c0f-a61a-7e1e184a4e59",
    DOMAIN: "auth-idp.qa.qa.assetwatch.com",
  },
  dev: {
    USER_POOL_ID: "us-east-2_x7NmjJEZB",
    APP_CLIENT_ID: "3urf9oqfossk0dv7eacrkl4p4h",
    IDENTITY_POOL_ID: "us-east-2:f2b106f4-7e58-4fe5-9576-59bfdd75cfe0",
    DOMAIN: "auth-idp.dev.dev.assetwatch.com",
  },
} satisfies Record<string, CognitoConfig>;

const requiredKeys = [
  "USER_POOL_ID",
  "APP_CLIENT_ID",
  "IDENTITY_POOL_ID",
  "DOMAIN",
] as const satisfies readonly (keyof CognitoConfig)[];

export function resolveAssetWatchCognitoConfig({
  allowFallback = false,
  deployEnvironment,
  injectedConfig,
}: {
  allowFallback?: boolean;
  deployEnvironment?: string;
  injectedConfig?: InjectedCognitoConfig;
}): CognitoConfig {
  const entries = requiredKeys.map((key) => [key, injectedConfig?.[key]] as const);
  const missingKeys = entries
    .filter(([, value]) => !value)
    .map(([key]) => `VITE_COGNITO_${key}`);

  if (injectedConfig && missingKeys.length !== entries.length) {
    if (missingKeys.length > 0) {
      throw new Error(
        `Incomplete Cognito config. Missing: ${missingKeys.join(", ")}`,
      );
    }

    return injectedConfig as CognitoConfig;
  }

  if (!allowFallback) {
    throw new Error(
      `Cognito config is required. Missing: ${missingKeys.join(", ")}`,
    );
  }

  const envKey = deployEnvironment as keyof typeof fallbackCognitoConfig;
  return fallbackCognitoConfig[envKey] ?? fallbackCognitoConfig.dev;
}
